/** One row of the Business Opportunities List view (IdeaTable), with its inline actions. */

import type { ReactNode } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Archive, ArchiveRestore, PauseCircle, RotateCw } from 'lucide-react';

import type { Dimension, Disposition, IdeaRow } from '@/api/types';
import { useSession } from '@/session/SessionContext';

import { DispositionPill, IndustryLabel, VerdictPill, useIdeaLink } from './ideaUi';
import { ScoreValue } from '@/ui/ScoreValue';
import { ResearchStatusPill, isPending } from '@/ui/ResearchStatusPill';

export type PendingIdeaAction =
  | { kind: 'rerun'; idea: IdeaRow }
  | { kind: 'disposition'; idea: IdeaRow; to: Disposition };

// A row's actions sit inline as icon buttons, each named by an instant tooltip.
type RowAction = { label: string; icon: ReactNode; disabled?: boolean; tooltip?: string; onSelect: () => void };

function rerunAction(idea: IdeaRow, busy: boolean, setPending: (p: PendingIdeaAction) => void): RowAction {
  return {
    label: 'Rerun research',
    icon: <RotateCw aria-hidden="true" />,
    disabled: busy,
    tooltip: busy ? 'Research is already queued or running' : undefined,
    onSelect: () => setPending({ kind: 'rerun', idea }),
  };
}

function triageActions(idea: IdeaRow, setPending: (p: PendingIdeaAction) => void): RowAction[] {
  if (idea.disposition === 'active') {
    return [
      { label: 'Park', icon: <PauseCircle aria-hidden="true" />, onSelect: () => setPending({ kind: 'disposition', idea, to: 'parked' }) },
      { label: 'Archive', icon: <Archive aria-hidden="true" />, onSelect: () => setPending({ kind: 'disposition', idea, to: 'archived' }) },
    ];
  }
  return [
    {
      label: 'Reactivate',
      icon: <ArchiveRestore aria-hidden="true" />,
      onSelect: () => setPending({ kind: 'disposition', idea, to: 'active' }),
    },
  ];
}

function useRowActions(idea: IdeaRow, busy: boolean, setPending: (p: PendingIdeaAction) => void): RowAction[] {
  const { user, can } = useSession();
  const canRerun = can('idea.research.rerun.any') || (idea.author.id === user?.id && can('idea.research.rerun.own'));
  return [
    ...(canRerun ? [rerunAction(idea, busy, setPending)] : []),
    ...(can('idea.triage') ? triageActions(idea, setPending) : []),
  ];
}

function VerdictCell({ idea, busy }: { idea: IdeaRow; busy: boolean }) {
  return (
    <td className="verdict-cell">
      {idea.research_status === 'scored' ? (
        <VerdictPill verdict={idea.verdict} />
      ) : (
        <>
          <ResearchStatusPill status={idea.research_status} />
          {idea.verdict && busy && (
            <span className="sub">Last run: <VerdictPill verdict={idea.verdict} /></span>
          )}
        </>
      )}
    </td>
  );
}

export function IdeaTableRow({
  idea,
  dimensions,
  setPending,
}: {
  idea: IdeaRow;
  dimensions: Dimension[];
  setPending: (p: PendingIdeaAction) => void;
}) {
  const navigate = useNavigate();
  const ideaLink = useIdeaLink();
  const busy = isPending(idea.research_status);
  const actions = useRowActions(idea, busy, setPending);
  return (
    <tr
      className="is-clickable"
      onClick={(e) => {
        // The title link, the action buttons and a text selection keep their own behaviour.
        if ((e.target as HTMLElement).closest('a, button') || window.getSelection()?.toString()) return;
        const link = ideaLink(idea.id);
        navigate(link.to, { state: link.state });
      }}
    >
      <td className="col-industry">
        <IndustryLabel idea={idea} compact />
      </td>
      <td className="col-idea">
        <Link {...ideaLink(idea.id)} className="idea-link">
          {idea.title}
        </Link>
      </td>
      <td>
        <DispositionPill disposition={idea.disposition} />
      </td>
      <VerdictCell idea={idea} busy={busy} />
      <td className="num total-cell">
        <ScoreValue value={idea.total} />
      </td>
      {dimensions.map((d) => (
        <td key={d.key} className="num dim-col">
          <ScoreValue value={idea.scores[d.key]} />
        </td>
      ))}
      <td>
        <div className="row-actions">
          {actions.map((a) => (
            <button
              key={a.label}
              type="button"
              className="btn btn-secondary btn-icon"
              data-tooltip={a.tooltip ?? a.label}
              aria-label={`${a.label}: ${idea.title}`}
              disabled={a.disabled}
              onClick={a.onSelect}
            >
              {a.icon}
            </button>
          ))}
        </div>
      </td>
    </tr>
  );
}
